/**
 * Sanitise a post-login return URL (`?uri=`) down to a same-origin path.
 *
 * The value travels in the query string, so it is attacker-supplied in the
 * ordinary sense: anyone can hand a victim a link to our own login page
 * carrying any `uri` they like. Without this, that is an open redirect wearing
 * our domain -- the phishing primitive, since the victim really is on
 * app.doorspot.com when they decide to trust the page.
 *
 * Only a single-slash absolute path on this origin survives. Everything else
 * returns null, and callers fall back to their normal destination rather than
 * failing the login.
 *
 * Rejected, and why each needs saying out loud:
 *  - `//evil.com` and `///evil.com` -- protocol-relative, so the browser reads
 *    the host as evil.com even though it starts with a slash.
 *  - `/\evil.com` and `/\\evil.com` -- browsers normalise a backslash to a
 *    forward slash in the authority position, so this is `//evil.com` with one
 *    extra step. A `startsWith('//')` check alone misses it.
 *  - `https://evil.com`, `javascript:...`, `data:...` -- anything not starting
 *    with `/` at all, which covers every scheme.
 *
 * Returns the DECODED path, ready to hand to `Router.navigateByUrl()`. A value
 * that is not decodable (a stray `%` makes `decodeURIComponent` throw) is
 * rejected rather than passed through half-decoded.
 */
export function safeReturnUrl(raw: string | null | undefined): string | null {
    if (!raw) {
        return null;
    }

    let decoded: string;
    try {
        decoded = decodeURIComponent(raw);
    } catch {
        // URIError from a malformed escape sequence. Not a path we can trust.
        return null;
    }

    // Must be an absolute path on this origin.
    if (!decoded.startsWith('/')) {
        return null;
    }

    // ...and exactly one leading slash, counting a backslash as one too: the
    // second character deciding the authority is the whole trick here.
    if (/^\/[\\/]/.test(decoded)) {
        return null;
    }

    // Control characters anywhere, and whitespace at either END, would let a
    // parser disagree with this check about where the URL starts and stops.
    // Interior whitespace is left alone: a query value may legitimately contain
    // a space, and `?q=a%20b` is an ordinary return URL, not an attack.
    if (/[\u0000-\u001f\u007f]/.test(decoded) || decoded !== decoded.trim()) {
        return null;
    }

    return decoded;
}
